import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  addCard,
  cardEntries,
  cardSignal,
  searchAll,
  type Definition,
} from "@/lib/search";
import { deleteNote, getNoteByTermOrNull } from "@/lib/note";
import { useRefreshOnFocus } from "@/lib/useRefreshOnFocus";
import { mergeCardSignal, type CardSignal } from "@/surfaces/read/cardSignal";
import { parseImageFilename } from "@/surfaces/read/mediaSlots";
import { shouldCollapse } from "@/surfaces/read/resultBadges";
import type { CardEntries } from "@/surfaces/read/onCard";

import type { ResultRow } from "./resultKind";

/**
 * Looking a word up, and everything that follows from having looked it up (日本-497).
 *
 * This is `Read.tsx`'s dock, lifted out whole so 検索 can be the same thing driven by a text field
 * rather than by a hovered token. Nothing here is new: every rule below arrived as a bug in Read
 * and its comment is the account of that bug, moved verbatim. **Read it before changing anything**
 * — several of these look like redundant bookkeeping and each one is load-bearing in a way that is
 * invisible when broken.
 *
 * **It lives in `components/` rather than in either surface** because it now belongs to both, and
 * `components/` is already where the cross-surface pieces are (`NoteFieldRow`, `TagChips`,
 * `Markdown`). It still imports `ResultCard`'s siblings from `surfaces/read/` in place: those are
 * genuinely Read's neighbours — `AddedPanel` reaches into the image slots — and moving them would
 * be churn for a story rather than for a caller.
 *
 * **What it deliberately does NOT own** is anything about where the query came from. Read's hover
 * pivot, its FORM strip, its radical picker, its sentence editing and its per-selection teardown all
 * stay in `Read.tsx`; 検索's field stays in `Search.tsx`. This owns the query's *answer*.
 */

/** Where to look: the text, and the character position to pivot on. */
export interface LookupQuery {
  text: string;
  index: number;
  /**
   * The word at `index` — Read's hovered token. Omitted by 検索, where the whole text is the word.
   *
   * **It is what the grammar dictionary is anchored on and what a dictionary row's exactness is
   * measured against** (日本-508). Both were the whole `text` before, which on Read is a sentence:
   * the grammar route then answered every construction anywhere in it — the same eight rows for
   * every word Alex hovered — and `vocabRank` compared a three-character match against a
   * thirty-character query, so no dictionary row could ever rank `exact` and the whole of that
   * grammar noise sorted above the definitions.
   *
   * **Taken as the string, sent as a LENGTH** — `anchorLength`, in code points. It was sent as a
   * string while the ranking ran here, on the reasoning that a UTF-16 slice of a code-point offset
   * is off by one for every surrogate pair before it. That reasoning was about slicing in JS and
   * does not carry to the server, which slices `text[index : index + anchorLength]` in Python,
   * where both the offset and the length are already code points — `index` is a `ParsedToken.start`
   * this same server produced. So the hook takes what Read already holds and sends the pivot the
   * two routes have always taken (日本-513).
   */
  term?: string;
}

/**
 * What a newly added card inherits.
 *
 * Read passes the sentence the lookup came from — the main labour-saver in the redesign, since every
 * card mined from a capture gets that capture's screenshot. 検索 passes nothing, and a card added
 * there has an empty `Sentence` and no images. That is correct rather than a gap: there is no
 * sentence, and the server writes `sentence or ""` with an empty `SentenceTarget` beside it.
 */
export interface MineSource {
  /** The sentence text, or null for a card with no sentence. */
  text: string | null;
  /** The two image slot values, raw — `mediaSlots.ts`'s invariant is that a slot holds a field value. */
  image: string;
  extraImage: string;
  /** Told the note id when a card is genuinely CREATED here — never on an append. */
  onMined?: (noteId: number) => void;
}

export interface UseLookupOptions {
  /**
   * Null means **there is no question**, and the answer goes with it: the results, the focus, the
   * `Entries` memo and any expanded card are all cleared, so the surface draws its own empty state.
   *
   * Both callers depend on that. 検索's `×` (and backspacing the field to nothing) sends null, and
   * leaving the last word's rows under an empty search box is a screen nobody asked a question of;
   * Read's `deleteEntry` sends null by clearing the selection, and its comment there says in so many
   * words that this is what empties the dock.
   */
  query: LookupQuery | null;
  /**
   * Terms on screen that are not results — Read's log.
   *
   * **Must be referentially stable**, because it joins the memo that decides what to ask the
   * collection about. Read passes a `useMemo` over its entries; 検索 passes nothing.
   */
  extraTerms?: readonly string[];
  /** Move the top bar's two counters by one, now. See `useDeckCounts`. */
  onAdjustCounts: (delta: number) => void;
  /**
   * Show one kind only (日本-519) — Read's すべて / 語彙 / 文法 filter.
   *
   * **Applied HERE rather than by the surface**, and that is the whole reason it is an option
   * instead of a `.filter()` at the call site. `focusedResult`, `busyRow` and `expanded.row` all
   * index `rows`, so a list filtered downstream of them puts the focus ring, the busy spinner and
   * the expanded panel on whichever row happens to sit at that index in the shorter list — a
   * different word. That is the bug `rows` vs `results` already documents, one layer out.
   *
   * `counts` is deliberately NOT filtered with it: the count line describes the whole answer,
   * because a count that shrank with the filter could not say whether the grammar dictionary had
   * answered at all, which is the one thing it exists for.
   */
  kind?: "all" | "vocab" | "grammar";
  /**
   * How long to wait before asking. Sweeping a pointer across a sentence must not fire a request
   * per character; a typed query wants longer than a hover.
   *
   * **The default is 45ms and the design's figure was 80** — a deliberate deviation, recorded here
   * rather than drifted into. 80 was right while the highlight moved with the pointer and only the
   * dock waited on this. Since 日本-512 the highlight follows the ANSWER, so the debounce sits on
   * the critical path of something the reader is watching, and it measured as a third of a 300ms
   * hover-to-highlight. At 45 a deliberate sweep still coalesces, and the server answers this route
   * in under 2ms — the debounce is protecting the render, not the backend.
   */
  debounceMs?: number;
}

/** The row expanded in place as a full card — reached by adding it, or by `E` on one you own. */
export interface ExpandedRow {
  term: string;
  /**
   * WHICH row was opened, by its index in `results`. The term alone stopped being enough at
   * 日本-290: a row is now an (entry × written form) pair and several rows can share a term — a 蝗
   * lookup renders three, one per entry, all headlined 蝗 — so anchoring the panel by term put it
   * over the FIRST of them whichever one you pressed. Still checked alongside the term, so a stale
   * index cannot open a panel over an unrelated row.
   */
  row: number;
  noteId: number;
  justAdded: boolean;
  image: string | null;
  /**
   * Bumped whenever the SERVER has rewritten this note, so the panel reloads it.
   *
   * An append lands on the note the panel is already showing, so `noteId` does not change — and the
   * panel's load effect keyed on `noteId` alone therefore never re-ran. Adding an archaic ねこま to
   * an open 猫 card wrote the new section to the collection and left the panel showing the card as
   * it was before, which reads as the append having failed.
   */
  revision: number;
}

export interface Lookup {
  /**
   * The DICTIONARY rows alone.
   *
   * Kept beside `rows` rather than folded into it because `add` and `edit` take a `Definition` and
   * the collection question is asked about dictionary terms — the two are different lists and
   * conflating them is how a grammar row ends up handed to a function that wants a JMdict seq.
   */
  results: Definition[];
  /**
   * What is actually rendered: both kinds, merged and ordered **by the server** — `/api/search/all`
   * (日本-513). It used to be `resultKind.mergeResults` over two separate answers.
   *
   * **`focusedResult`, `busyRow` and `expanded.row` all index into THIS**, because they name a
   * position in the list on screen. Indexing `results` instead would put the focus ring and the
   * expanded panel on the wrong row the moment a grammar entry sorts above a dictionary one.
   */
  rows: ResultRow[];
  /**
   * Every row the query answered with, before `kind` narrowed it (日本-519).
   *
   * What the COUNT line reads, and what decides whether the filter is drawn at all — both are
   * questions about the answer rather than about the view, and asking them of the filtered list
   * would make 文法 hide the very row that justifies offering it.
   */
  allRows: ResultRow[];
  /**
   * The run of text the dock is actually answering about: where it starts and how long it is.
   *
   * **Both halves together, and that is the point.** Read draws its highlight from this
   * (日本-512): the pivot is a character, so the matched run can start inside one token and finish
   * inside the next — hovering the 道 of 弓道 answers 道場, across the 弓道/場 boundary.
   *
   * It used to be the LENGTH alone, paired at the call site with the live pivot — and those two
   * update at different moments, which made the highlight bounce. The pivot moves with the
   * pointer; the length arrives a debounce and a round trip later. In between, the highlight was
   * assembled from two different lookups and painted a span that was not a word. Traced over one
   * sweep of `この豪華な弓道場がある。`: **16 highlight states across 11 characters**, of which
   * `道場が`, `場が`, `華な` and `があ` existed only because the new pivot was wearing the previous
   * word's length.
   *
   * Answering with the pair makes that unspellable: the index is the one the length was computed
   * for, so every state the reader sees is a run the server really matched.
   *
   * `length` is at least 1 even when nothing matched, so a pivot over a word the dictionary does
   * not know still shows where the pointer is rather than blinking out.
   *
   * Null while there is no question at all.
   */
  match: { index: number; length: number } | null;
  /** The design's density rule: at nine or more rows every result collapses and the focused one expands. */
  collapse: boolean;
  focusedResult: number;
  setFocusedResult: React.Dispatch<React.SetStateAction<number>>;
  /** What the collection knows about each term on screen. */
  carded: ReadonlyMap<string, CardSignal>;
  /** Which entries each shown term's existing card already holds, keyed by term. */
  onCardEntries: CardEntries;
  /** Which ROW is mid-add, by its index in `rows` — never by term. */
  busyRow: number | null;
  expanded: ExpandedRow | null;
  setExpanded: React.Dispatch<React.SetStateAction<ExpandedRow | null>>;
  /** A failed add/undo/lookup. Shown by the surface; a button that silently un-busies reads as "added". */
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  add: (definition: Definition, row: number, source?: MineSource | null) => Promise<void>;
  /**
   * `+ 追加` on a 文法 row (日本-498).
   *
   * Separate from `add` rather than a branch inside it, because the two share almost nothing: no
   * term to resolve, no `entrySeq`, no `onCardEntries` bookkeeping (a grammar card holds one DoJG
   * entry and can never gain a second), and no `AddedPanel` — the row is already expandable in
   * place, so expanding it again on success would replace the entry the reader just carded with a
   * card editor built for `Genki (Apoc)`'s six fields.
   *
   * What it DOES share is every rule about the collection question: the optimistic `known`, the
   * `noteLocalChange` guard that stops an in-flight `card-signal` reverting the row, the forced
   * re-ask, and the ±1 on the deck counters.
   */
  /**
   * Open the card filed under `term` in the expand-in-place panel.
   *
   * Takes the TERM rather than a `Definition`, so a 文法 row can be edited by the same call — its
   * headword is a DoJG concept, and the panel only ever wanted the string.
   */
  edit: (term: string, row: number) => Promise<void>;
  undo: () => Promise<void>;
  /** Re-ask the collection about everything on screen, superseding anything in flight. */
  refreshSignals: () => void;
}

export function useLookup({
  query,
  extraTerms,
  onAdjustCounts,
  kind = "all",
  debounceMs = 45,
}: UseLookupOptions): Lookup {
  /**
   * What is drawn: both kinds, merged and ordered **by the server** (日本-513).
   *
   * State rather than a `useMemo` over two lists, because the order is now something that arrives
   * rather than something derived here. `results` is kept beside it — the dictionary half, in the
   * merged order — because `card-entries` is asked about dictionary terms and nothing else, and
   * re-deriving that on every render of an unrelated state change would rebuild the array each
   * time.
   */
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [results, setResults] = useState<Definition[]>([]);
  /**
   * The run the dock is answering about — see `Lookup.match`.
   *
   * State rather than something derived from `results` and the live query, because the whole point
   * is that it must NOT move when the pivot does. It is written in one place, beside the results
   * it belongs to, so the index and the length can never come from different lookups.
   */
  const [match, setMatch] = useState<{ index: number; length: number } | null>(null);
  /**
   * Which JMdict entries the existing card for each shown term already holds, so a row can say
   * `✓ on card` rather than offering to add what is already there.
   *
   * Fetched per LOOKUP, not per hover like `card-signal`: rows only exist for the word being looked
   * up, so asking for every term on screen would be a request per mouse move to answer a question
   * about one of them. An empty answer means "nothing recorded" and every row offers to add — the
   * server refuses a real duplicate regardless, so a wrong guess costs a message, not a bad write.
   *
   * **Kept keyed by term, and asked per term** (`isOnCard`). It used to be flattened into one set of
   * every seq in the answer, which 日本-290 turned into a live bug: a row is now an (entry × written
   * form) pair, so the 覇王樹 / 仙人掌 / サボテン rows share seq 1387180 by construction and carding
   * one flipped all three to `✓ on card`.
   */
  const [onCardEntries, setOnCardEntries] = useState<CardEntries>({});
  const [focusedResult, setFocusedResult] = useState(0);
  const [carded, setCarded] = useState<ReadonlyMap<string, CardSignal>>(new Map());
  /**
   * Which ROW is mid-add, by its index in `results` — not by term. The fan-out lets several rows
   * share a term: 蝗 sits in three entries, so a 蝗 lookup renders three rows all headlined 蝗 (rice
   * grasshopper / grasshopper / rice pest). Keyed by term, pressing add on one spun all three.
   * `results` is replaced wholesale on every lookup and this is cleared with it, so an index cannot
   * outlive the list it points into.
   */
  const [busyRow, setBusyRow] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<ExpandedRow | null>(null);
  /** Monotonic, so a second append to the same note is a different `revision` than the first. */
  const noteRevision = useRef(0);
  const [error, setError] = useState<string | null>(null);

  // "On screen" is the caller's own terms AND the results. Results are the half that is easy to
  // miss: looking up よう returns 形, 酔う, 用 … none of which are in the sentence, so asking only
  // about the log left every one of them claiming `not in collection` however long you had owned it.
  const onScreen = useMemo(() => {
    const terms = new Set(extraTerms ?? []);
    for (const entry of results) if (entry.term) terms.add(entry.term);
    // **Grammar concepts too** (日本-498), and the omission was silent in the worst way: the server
    // answered `known` for a carded construction, nothing asked, and the row drew 未登録 beside a
    // `+ 追加` that then refused as a duplicate. The concept is asked VERBATIM, markup and all —
    // that is the string `Japanese` holds, so anything normalised here would ask about a card that
    // does not exist.
    for (const row of rows) {
      if (row.kind === "grammar" && row.entry.concept) terms.add(row.entry.concept);
    }
    return [...terms];
  }, [extraTerms, results, rows]);

  /** Terms already asked about. Hovering re-renders constantly; re-asking per hover would not. */
  const asked = useRef<Set<string>>(new Set());
  /**
   * When each term was last changed locally, against a monotonic clock.
   *
   * A response issued before an add or an undo is out of date about exactly that term — `known` for
   * one just added, `unknown` for one just undone — and landing late it flipped `✓ 収録済` back to
   * `+ 追加` a second after the add appeared to work. Per term rather than per response, because a
   * term is asked about only once: discarding a whole batch would strand its other terms with no
   * answer.
   *
   * Two things stamp here. An add or an undo stamps the one term it changed. A *forced* re-ask
   * stamps everything it is about to ask about, because it is issued in response to the collection
   * changing and therefore outranks whatever was already in flight.
   */
  const cardedGen = useRef(0);
  const changedAt = useRef(new Map<string, number>());
  const noteLocalChange = useCallback((term: string) => {
    changedAt.current.set(term, ++cardedGen.current);
  }, []);
  /** The live term list, for the focus refresh — which must not re-register on every hover. */
  const onScreenRef = useRef(onScreen);
  onScreenRef.current = onScreen;

  const askCardStatus = useCallback((terms: readonly string[], force = false) => {
    const fresh = force ? [...terms] : terms.filter((term) => !asked.current.has(term));
    if (fresh.length === 0) return;
    fresh.forEach((term) => asked.current.add(term));
    if (force) {
      // A forced re-ask is issued BECAUSE the collection just changed, so it supersedes anything
      // already in flight for the terms it covers — including the ones whose truth changed
      // *transitively*, which the per-term stamps cannot know about: undoing the card filed under
      // 沢山 also un-cards the たくさん that resolves to it, and only the server knows that pairing.
      // Skipping those older responses strands nothing, which is what makes this safe where
      // discarding a response in general is not — this request answers every term it silences.
      const supersedes = ++cardedGen.current;
      fresh.forEach((term) => changedAt.current.set(term, supersedes));
    }
    const gen = cardedGen.current;

    void cardSignal(fresh)
      // Deliberately NOT cancelled when the terms on screen change. The merge is order-independent
      // (see `mergeCardSignal`), so a late response is harmless — while discarding one was not:
      // `fresh` is marked asked before the request resolves, and the un-ask below runs only on
      // failure, so a response dropped after resolving left every term in it permanently
      // unasked-about and rendering as "not in collection" until the surface unmounted.
      //
      // `> gen`, strictly. A forced re-ask stamps every term it covers at `supersedes` and then
      // reads `cardedGen.current`, which IS `supersedes` — so `>=` would have every forced re-ask
      // silently discard its own response and leave the optimistic guess standing forever.
      .then((response) =>
        setCarded((current) =>
          mergeCardSignal(current, response, (term) => (changedAt.current.get(term) ?? 0) > gen),
        ),
      )
      // This is the ONE call here that needs the sync hub. When the hub is down every word simply
      // reads as "not in collection" — looking up and radical lookup keep working, which is the
      // difference between "cannot add cards" and "cannot read".
      .catch(() => {
        // Let them be retried; a hub blip should not mark them known-unknown for the session.
        fresh.forEach((term) => asked.current.delete(term));
      });
  }, []);

  useEffect(() => {
    askCardStatus(onScreen);
  }, [onScreen, askCardStatus]);

  const refreshSignals = useCallback(() => {
    askCardStatus(onScreenRef.current, true);
  }, [askCardStatus]);

  // Terms are otherwise asked about exactly once per mount, with no TTL — so a card added on the
  // phone or in Anki desktop never reached the underlines until a rail click unmounted the surface.
  const cardSignalMounted = useRef(false);
  useRefreshOnFocus(() => {
    // The hook fires on mount too, which the effect above already covers — and firing both would
    // send the opening query's terms twice.
    if (!cardSignalMounted.current) {
      cardSignalMounted.current = true;
      return;
    }
    refreshSignals();
  });

  // The pivot. Debounced, as the design specifies, so sweeping across a sentence does not fire a
  // request per character.
  const { text, index, term } = query ?? { text: "", index: -1, term: undefined };
  /**
   * How many characters at `index` the reader actually asked about — the hovered token's length,
   * or `undefined` for a typed query, where the whole text IS the word.
   *
   * Counted in CODE POINTS, because that is what the server counts — `matchLength` and
   * `anchorLength` are both Python `len`, where a surrogate pair is one character and JS `.length`
   * says two.
   *
   * **This is the whole of 日本-508 as far as this hook is concerned now.** The ranking used to
   * need the anchor TEXT as well, to measure a row's exactness against the word rather than the
   * sentence; since the ordering moved to the server (日本-513) it derives that from these two
   * numbers itself, so the hook sends the pivot and nothing re-derives the word on this side.
   */
  const anchorLength = term === undefined ? undefined : [...term].length;
  const hasQuery = query !== null;
  useEffect(() => {
    // **A new question closes the card opened over the old answer.** The panel is anchored by
    // `(term, row)` into a list that is about to be replaced, so leaving it set is not merely
    // untidy: `undo` is a hard `deleteNote`, and on 検索 — where nothing else clears it — adding a
    // card and then typing anything left Ctrl+Z deleting a note with no panel on screen, on the one
    // surface where the caret is in a text field and Ctrl+Z means *undo my typing*. Retyping the
    // same word could also re-open the `justAdded` panel over a card that was added minutes ago.
    // Read never saw either, because its own `selectionKey` effect already clears this.
    setExpanded(null);
    if (!hasQuery) {
      // No question, no answer. See `UseLookupOptions.query`.
      setRows((current) => (current.length === 0 ? current : []));
      setResults((current) => (current.length === 0 ? current : []));
      setMatch(null);
      setFocusedResult(0);
      setBusyRow(null);
      setOnCardEntries((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    // The timeout guard alone is not enough: a slow response for word A landing after word B's
    // would show A's definitions under B's header — and `A` would add the wrong card.
    let stale = false;
    const handle = window.setTimeout(() => {
      // **One request, already ordered** (日本-513). This was `Promise.all` over `/lookup` and
      // `/grammar` plus a client-side `mergeResults`; the ordering now lives on the server so the
      // MCP's `search` tool and this screen cannot disagree about it.
      //
      // Two properties the two-request version had are kept for free rather than by care, which is
      // most of why the change is safe: the two halves land in **one** commit (there is only one
      // response), so the merged order cannot visibly reshuffle under the reader; and a dataset
      // built before `schema_version` 4 answers no grammar rather than failing, because
      // `GrammarIndex.search` already degrades that way and `/all` calls it exactly as `/grammar`
      // did. What is genuinely gone is the ability for grammar to fail *independently* — a 500 from
      // the merged route takes the dictionary with it. That is the accepted cost, and the route
      // does nothing either half did not already do.
      void searchAll(text, index, anchorLength)
        .then(({ rows: merged }) => {
          if (stale) return;
          const found = merged.flatMap((row) => (row.kind === "vocab" ? [row.entry] : []));
          setRows(merged);
          setResults(found);
          // **Written here, with the results, and nowhere else.** `index` is this request's pivot,
          // captured in the closure, so the pair is always one lookup's own answer. Floored at one
          // character: a pivot over a word the dictionary does not know still has to show where
          // the pointer is, or the highlight blinks out over every unknown word and reads as the
          // app having stopped responding.
          //
          // `results` is the vocabulary half of the merged list in the server's own order —
          // exact-first, then longest-match-first — so its head is the longest real match here.
          // Deliberately not `merged[0]`: a grammar row can lead the list and its match is
          // measured against the key rather than against the sentence, so its length would light
          // a run of text that is not what matched.
          setMatch({ index, length: Math.max(1, found[0]?.matchLength ?? 1) });
          setFocusedResult(0);
          // Cleared with the list it indexes into. An add still in flight resolves into a
          // `setBusyRow(null)` that is already a no-op, which is right: the row it was spinning no
          // longer exists, and the index would otherwise point at an unrelated row.
          setBusyRow(null);
          setError(null);
          const terms = [...new Set(found.map((f) => f.term).filter(Boolean))] as string[];
          if (terms.length === 0) {
            setOnCardEntries({});
            return;
          }
          void cardEntries(terms)
            .then((byTerm) => {
              if (stale) return;
              setOnCardEntries(byTerm);
            })
            // A failure here costs a label, not a write: every row falls back to offering to add,
            // and the server still refuses a duplicate. Not worth a visible error.
            .catch(() => {
              if (!stale) setOnCardEntries({});
            });
        })
        .catch(() => {
          if (stale) return;
          setRows([]);
          setResults([]);
          setFocusedResult(0);
          setBusyRow(null);
          setOnCardEntries({});
          setError("検索に失敗しました。バックエンドが再起動中かもしれません。");
        });
    }, debounceMs);
    return () => {
      stale = true;
      window.clearTimeout(handle);
    };
  }, [hasQuery, text, index, anchorLength, debounceMs]);

  /**
   * `E` on a result you already own. The design gives `✓ 収録済` an `E` glyph, so the control
   * advertises this.
   */
  const edit = useCallback(async (term: string, row: number) => {
    // **A TERM, not a `Definition`** (日本-498). It was only ever read for its `term`, and a
    // grammar row's headword is its DoJG concept rather than a dictionary entry — so narrowing the
    // parameter to what is actually used is what lets `E` serve both kinds with no second path.
    if (!term) return;
    setBusyRow(row);
    try {
      const note = await getNoteByTermOrNull(term);
      if (note === null) {
        // `✓ 収録済` with nothing behind it means the card went away since the status check.
        setError(`${term} はコレクションにありません。`);
      } else if (!note.editable) {
        // Findable but not ours: `by-term` searches `Japanese:<term>`, a FIELD search, so a note of
        // another notetype carrying that field turns up here. Since 日本-498 `editable` means "this
        // app has an editor design for that notetype" — one notetype since 日本-519, so a
        // genuinely foreign one — `Names (Apoc)`, `Kanji (Apoc)` — lands here.
        setError(`${term} は ${note.noteModel} のノートです。編集は単語で開いてください。`);
      } else {
        setExpanded({
          term,
          row,
          noteId: note.noteId,
          justAdded: false,
          image: note.image.filename,
          revision: (noteRevision.current += 1),
        });
      }
    } catch (err) {
      // Was a silent empty catch, so `E` on anything it could not resolve did nothing at all —
      // indistinguishable from a dead key. The 409 for a foreign notetype lands here too, and its
      // message names the notetype, which is the most useful thing available.
      setError(
        err instanceof Error ? err.message : "カードを開けませんでした。同期ハブは動いていますか？",
      );
    } finally {
      setBusyRow(null);
    }
  }, []);

  const add = useCallback(
    async (definition: Definition, row: number, source?: MineSource | null) => {
      if (!definition.term) return;
      setBusyRow(row);
      setError(null);
      try {
        const result = await addCard({
          term: definition.term,
          reading: definition.primaryReading,
          // WHICH row was pressed. Every entry written this way shares one card, so this decides
          // which definition leads it — `(term, reading)` cannot say, since 位 has two entries
          // reading くらい.
          //
          // `includeArchaic` is deliberately NOT sent, even for an archaic row. The seq alone is
          // consent for the row that was pressed — `Definer.merge`'s `asked_for` rescues the clicked
          // entry from the rare filter by itself — while the flag means "merge EVERY archaic entry
          // written this way", which is a different and much larger claim: pressing one archaic 妾
          // row would drag in the other one, and 頭 would gain 2856438.
          entrySeq: definition.seq,
          sentence: source?.text ?? null,
          // Inherited from the sentence — the main labour-saver in the design. Empty on 検索, which
          // has no sentence to inherit from.
          image: source?.image ?? "",
          extraImage: source?.extraImage ?? "",
        });
        if (result.succeeded || result.duplicate) {
          // Same invalidation as the counter below, and for the same reason — a `card-signal` issued
          // before this card existed answers `unknown` for it, and landing late it would revert the
          // row to `+ 追加`.
          noteLocalChange(definition.term);
          // `known` with NO carrier: a row that read "carded as こんにちは" a moment ago now has a
          // card of its own, and leaving the carrier behind would print that badge next to
          // `✓ 収録済`. The forced re-ask below is still needed — it is the only thing that knows
          // which OTHER words on screen this card just made `elsewhere`.
          setCarded((current) => new Map(current).set(definition.term!, { state: "known" }));
          if (result.succeeded && result.noteId !== null) {
            // An APPEND is not an add. The card was already there, so no card was created today and
            // no new card entered the queue — and above all `justAdded` must stay false, because the
            // panel's Undo is `deleteNote(noteId)` and that noteId is now a card the user has been
            // reviewing for years.
            const appended = result.appended === true;
            // The card for THIS term now holds this definition either way, so stop offering to add
            // it again — under that term only. A sibling row spelling the same entry differently
            // still has no card of its own and must keep offering.
            setOnCardEntries((current) => {
              const held = current[definition.term!] ?? [];
              if (held.includes(definition.seq)) return current;
              return { ...current, [definition.term!]: [...held, definition.seq] };
            });
            if (!appended) {
              // Both counters, up by one, now — and any refetch already in flight is invalidated
              // with them, because it was issued before this card existed and landing late it would
              // erase the bump. That guard lives with the fetch in `useDeckCounts`.
              onAdjustCounts(1);
              source?.onMined?.(result.noteId);
            }
            // The optimistic set above only knows the HEADWORD the card was filed under, and the
            // sentence underline is drawn from the token — たくさん against a card stored as 沢山. So
            // re-ask now that the collection has actually changed: the server resolves the two to
            // each other, and this is the one moment it has a new answer. Ordered after
            // `noteLocalChange`, so the guard treats this response as current rather than stale.
            refreshSignals();
            // Expand in place: the result BECOMES the finished card, same position on screen.
            setExpanded({
              term: definition.term,
              row,
              noteId: result.noteId,
              justAdded: !appended,
              // Only a FRESH card inherits the sentence's screenshot. An append joined a card that
              // already has its own image, and handing the panel this capture's filename shows a
              // picture from the current sentence as that card's thumbnail until `getNote` lands.
              // `edit`, the other way into the panel over a pre-existing card, passes the card's own
              // `note.image.filename` for exactly this reason.
              image: !appended && source?.image ? parseImageFilename(source.image) : null,
              // An append rewrote a note the panel may already be showing, and `noteId` is unchanged
              // — so this is the only thing that tells it to read the card again.
              revision: (noteRevision.current += 1),
            });
            // Whether audio landed is reported by the panel's badge, NOT here. A message set here is
            // erased ~80ms later by the re-lookup that mining triggers, so it would never be read.
          } else if (result.duplicate) {
            // Was a silent success: the button flipped to `✓ 収録済` and nothing else happened. Since
            // `A` is only offered on a `+ 追加` row, reaching here means the UI believed you did not
            // have it — the one case that most needs a reason.
            setError(result.message ?? `${definition.term} はすでに収録済です。`);
            // And open it, which is almost certainly what you wanted next. It has to go through
            // `edit` rather than `setExpanded`: this branch carries no noteId. Awaited so the two
            // `busyRow` clears nest instead of racing — and a more specific failure from `edit` is
            // allowed to replace the message above.
            await edit(definition.term, row);
          }
        } else {
          // The server's designed failure channel: succeeded=false with a reason.
          setError(result.message ?? `${definition.term} を追加できませんでした。`);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : `${definition.term} を追加できませんでした。`,
        );
      } finally {
        setBusyRow(null);
      }
    },
    [noteLocalChange, refreshSignals, edit, onAdjustCounts],
  );

  const undo = useCallback(async () => {
    // Only ever undoes an add from this session — `E` opens the same panel without offering it.
    if (!expanded?.justAdded) return;
    try {
      await deleteNote(expanded.noteId);
    } catch (err) {
      // The card still exists; saying so beats a panel that closes while claiming nothing.
      setError(
        err instanceof Error ? err.message : "取り消しに失敗しました。カードは残っています。",
      );
      return;
    }
    // The mirror of the add: a `card-signal` issued while the card still existed answers `known`,
    // and landing after the delete it would put the term back.
    noteLocalChange(expanded.term);
    // Optimistically `unknown`, and this one can be WRONG in a way a boolean never was: deleting the
    // 覇王樹 card while a サボテン card still records the same entry moves it to `elsewhere`, not to
    // nothing. The forced re-ask below corrects it — a dotted-to-dashed flicker rather than a lie.
    // Written rather than deleted: absence means "never asked".
    setCarded((current) => new Map(current).set(expanded.term, { state: "unknown" }));
    onAdjustCounts(-1);
    setExpanded(null);
    // The mirror of the add's re-ask: the headword is gone, and so is whatever resolved to it.
    refreshSignals();
  }, [expanded, noteLocalChange, refreshSignals, onAdjustCounts]);

  // Narrowed for the caller, and every index below is computed against THIS. See the `kind` option.
  const shown = useMemo(
    () => (kind === "all" ? rows : rows.filter((row) => row.kind === kind)),
    [rows, kind],
  );

  /**
   * **The focus goes back to the top whenever the narrowing changes it out from under itself.**
   *
   * `focusedResult` names a position in `shown`, and only a new lookup resets it — so pressing
   * Read's 語彙 / 文法 filter with the focus five rows down left the index past the end of a
   * one-row list. `rows[focusedResult]` is then `undefined`, `hasFocus` is false, and `j`, `k`,
   * `A` and `O` all become no-ops with no row drawing a focus ring and nothing on screen saying
   * why. Clamped rather than remapped: the row that was focused may not be in the filtered list at
   * all, and the top is the only answer that is always a row.
   */
  useEffect(() => {
    setFocusedResult((at) => (at < shown.length ? at : 0));
  }, [shown]);

  return {
    results,
    rows: shown,
    allRows: rows,
    match,
    // The density rule counts what is ON SCREEN, so it counts the merged list — a query answering
    // six dictionary rows and four grammar entries is a ten-row screen, whichever half is which.
    collapse: shouldCollapse(shown.length),
    focusedResult,
    setFocusedResult,
    carded,
    onCardEntries,
    busyRow,
    expanded,
    setExpanded,
    error,
    setError,
    add,
    edit,
    undo,
    refreshSignals,
  };
}
